import ReportService from "../../../services/reports/Report.service";
import YoungworldReportService from "../../../services/reports/YoungworldReport.service";
import { useState, useEffect, useRef, useMemo } from "react";
import { OtbRawData } from "./YoungWorldTypes";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";
import ReportTable from "../ReportTable.component";
import EpochMonthService from "../../../services/EpochMonth.service";
import * as XLSX from "xlsx";
import { BaseReportParsedData, MoneyDataItem } from "../../../interfaces/reports/BaseReport.interface";
import type { EmployeeFilter } from "../../../services/reports/YoungworldReport.service";

interface YoungworldOtbReportProps {
  clientId: string | number;
  epochMonth?: number | null;
}

interface YoungworldParsedData extends BaseReportParsedData {
  categorySortOrder?: number | null;
  otbTrended: MoneyDataItem;
  month1Otb: MoneyDataItem;
  month2Otb: MoneyDataItem;
  month3Otb: MoneyDataItem;
  month4Otb: MoneyDataItem;
  month5Otb: MoneyDataItem;
  month6Otb: MoneyDataItem;
  month7Otb: MoneyDataItem;
  month8Otb: MoneyDataItem;
  month9Otb: MoneyDataItem;
}

const HIERARCHY_OPTIONS = {
  "Company": ["category", "locationCode"] as const,
  "By Location": ["locationCode"] as const,
  "By Category": ["category"] as const,
  "Category by Class by Location": ["category", "classCode", "locationCode"] as const,
};

function num(v: any): number { return (v === undefined || v === null || isNaN(Number(v))) ? 0 : Number(v); }

function monthLabel(epoch: number): string {
  // Use full month then trim to first 3 chars for MMM format
  const full = EpochMonthService.epochMonthToMMMM(epoch);
  return full.substring(0, 3);
}

function parseRaw(rows: OtbRawData[], selectedEpoch: number): YoungworldParsedData[] {
  // Group by locationCode|classCode|category
  const map = new Map<string, { base: OtbRawData | null; months: Record<number, OtbRawData> }>();
  rows.forEach(r => {
    const key = `${r.locationCode}|${r.classCode}|${r.category || ''}`;
    if (!map.has(key)) map.set(key, { base: null, months: {} });
    const entry = map.get(key)!;
    entry.months[r.epochMonthSpan] = r;
    if (r.epochMonthSpan === selectedEpoch) entry.base = r;
  });

  const parsed: YoungworldParsedData[] = [];
  map.forEach((entry) => {
    const base = entry.base;
    let monthVals: number[] = [];
    for (let offset = 1; offset < 9; offset++) {
      const epoch = selectedEpoch + offset;
      const raw = entry.months[epoch];
      const v = raw ? num(raw.otbRetail) : 0;
      monthVals.push(v);
    }

    // Trended OTB calculation requires data from selected month (offset 0)
    let trended = 0;
    if (base) {
      trended = num(ReportService.calculateTrendedOtb({
        invBomNM: num(base.inventoryBomPlanNm),
        currInv: num(base.inventoryCurrent),
        sales: num(base.sales),
        salesPlan: num(base.salesPlan),
        mdRemaining: num(base.markdownsRemaining),
        onOrder: num(base.onOrderRetail),
        trend: num(base.trend),
        format: false,
      }));
    }

    // Use first available row to fill identifying fields
    const firstRow = base || Object.values(entry.months)[0];
    if (!firstRow) return; // safety

    parsed.push({
      locationCode: firstRow.locationCode,
      locationName: firstRow.locationName,
      classCode: firstRow.classCode,
      className: firstRow.className,
      category: firstRow.category ?? 'N/A',
      categorySortOrder: (firstRow as any).categorySortOrder ?? null,
      parentClientClassCode: null,
      trend: firstRow.trend ?? 0,
      otbTrended: new MoneyDataItem(trended),
      month1Otb: new MoneyDataItem(monthVals[0]),
      month2Otb: new MoneyDataItem(monthVals[1]),
      month3Otb: new MoneyDataItem(monthVals[2]),
      month4Otb: new MoneyDataItem(monthVals[3]),
      month5Otb: new MoneyDataItem(monthVals[4]),
      month6Otb: new MoneyDataItem(monthVals[5]),
      month7Otb: new MoneyDataItem(monthVals[6]),
      month8Otb: new MoneyDataItem(monthVals[7]),
      month9Otb: new MoneyDataItem(monthVals[8]),
    });
  });

  return parsed;
}

const YoungworldOtbReport = ({ clientId, epochMonth }: YoungworldOtbReportProps) => {
  const [selectedEpochMonth, setSelectedEpochMonth] = useState<number>(epochMonth ?? EpochMonthService.getTodayEpochMonth());
  const rawRef = useRef<OtbRawData[]>([]);
  const [parsed, setParsed] = useState<YoungworldParsedData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hierarchyKey, setHierarchyKey] = useState<keyof typeof HIERARCHY_OPTIONS>("Company");
  const [employee, setEmployee] = useState<EmployeeFilter>('none');
  const hierarchy = HIERARCHY_OPTIONS[hierarchyKey] as readonly string[];

  const maxSelectableEpochMonth = useMemo(() => EpochMonthService.getTodayEpochMonth() + 1, []);

  // Clamp to max month allowed (1 month ahead of today)
  useEffect(() => {
    if (selectedEpochMonth > maxSelectableEpochMonth) {
      setSelectedEpochMonth(maxSelectableEpochMonth);
    }
  }, [selectedEpochMonth, maxSelectableEpochMonth]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await YoungworldReportService.getOtbReportData({ clientId, epochMonth: selectedEpochMonth });
        // API returns array inside data? Service returns response.data root; adapt if shaped {data: []}
        const rows: OtbRawData[] = Array.isArray((data as any)?.data) ? (data as any).data : (data as OtbRawData[]);
        rawRef.current = rows || [];
        const _parsed = parseRaw(rawRef.current, selectedEpochMonth);
        setParsed(_parsed);
      } catch (e) {
        console.error(e);
        setParsed([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId, selectedEpochMonth]);

  // Default hierarchy to Category by Class by Location when an employee filter is active
  useEffect(() => {
    if (employee !== 'none') {
      setHierarchyKey('Category by Class by Location');
    }
  }, [employee]);

  const filtered = useMemo(() => {
    const classCodes = YoungworldReportService.getEmployeeClassCodes(employee);
    const employeeFiltered = (classCodes.length > 0) ? parsed.filter(p => classCodes.includes(Number(p.classCode))) : parsed;
    return ReportService.sortAndFilterData(employeeFiltered, [...hierarchy], [], [], []);
  }, [parsed, hierarchy, employee]);

  const monthEpochs = useMemo(() => Array.from({ length: 9 }, (_, i) => selectedEpochMonth + i), [selectedEpochMonth]);

  const columns = useMemo(() => {
    const hierarchyColumns = hierarchy.map(h => ({ name: h === 'locationCode' ? 'Location' : h === 'category' ? 'Category' : h === 'classCode' ? 'Class' : h, sortable: false }));
    const mLabels = monthEpochs.map(e => monthLabel(e));
    const cols: { name: string; sortable: boolean }[] = [
      ...hierarchyColumns,
      { name: 'OTB Trended', sortable: false },
      { name: `${mLabels[1]} OTB`, sortable: false },
      { name: 'OTB 2 Months', sortable: false },
      { name: `${mLabels[2]} OTB`, sortable: false },
      { name: 'OTB 3 Months', sortable: false },
      { name: `${mLabels[3]} OTB`, sortable: false },
      { name: 'OTB 4 Months', sortable: false },
      { name: `${mLabels[4]} OTB`, sortable: false },
      { name: 'OTB 5 Months', sortable: false },
      { name: `${mLabels[5]} OTB`, sortable: false },
      { name: 'OTB 6 Months', sortable: false },
      { name: `${mLabels[6]} OTB`, sortable: false },
      { name: 'OTB 7 Months', sortable: false },
      { name: `${mLabels[7]} OTB`, sortable: false },
      { name: 'OTB 8 Months', sortable: false },
      { name: `${mLabels[8]} OTB`, sortable: false },
      { name: 'OTB 9 Months', sortable: false },
    ];
    return cols;
  }, [hierarchy, monthEpochs]);

  const tableData = useMemo(() => {
    return filtered.map(r => {
      const labels = ReportService.hierarchyItemToVerticalDisplayNames(r, [...hierarchy]);
      const trended = num(r.otbTrended.value);
      const m = [num(r.month1Otb.value), num(r.month2Otb.value), num(r.month3Otb.value), num(r.month4Otb.value), num(r.month5Otb.value), num(r.month6Otb.value), num(r.month7Otb.value), num(r.month8Otb.value), num(r.month9Otb.value)];
      const cum: number[] = [];
      for (let n = 2; n <= 9; n++) {
        let sum = trended;
        for (let i = 0; i < n - 1 && i < m.length - 1; i++) {
          sum += m[i];
        }
        cum.push(sum);
      }
      return [
        ...labels,
        ReportService.formatCurrency(trended),            // OTB Trended
        ReportService.formatCurrency(m[0]),               // MMM selected
        ReportService.formatCurrency(cum[0]),             // OTB 2 Months
        ReportService.formatCurrency(m[1]),               // MMM next
        ReportService.formatCurrency(cum[1]),             // OTB 3 Months
        ReportService.formatCurrency(m[2]),               // MMM
        ReportService.formatCurrency(cum[2]),             // OTB 4 Months
        ReportService.formatCurrency(m[3]),               // MMM
        ReportService.formatCurrency(cum[3]),             // OTB 5 Months
        ReportService.formatCurrency(m[4]),               // MMM
        ReportService.formatCurrency(cum[4]),             // OTB 6 Months
        ReportService.formatCurrency(m[5]),               // MMM
        ReportService.formatCurrency(cum[5]),             // OTB 7 Months
        ReportService.formatCurrency(m[6]),               // MMM
        ReportService.formatCurrency(cum[6]),             // OTB 8 Months
        ReportService.formatCurrency(m[7]),               // MMM
        ReportService.formatCurrency(cum[7]),             // OTB 9 Months
      ];
    });
  }, [filtered, hierarchy]);

  const exportToExcel = () => {
    try {
      const header = columns.map(c => c.name);
      const ws = XLSX.utils.aoa_to_sheet([header, ...tableData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'YoungworldOTB');
      const fileName = `Youngworld_OTB_${EpochMonthService.epochMonthToMMMYY(selectedEpochMonth)}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center max-w-[1700px]">
      <h2 className="text-2xl font-bold mb-2">
        {`${EpochMonthService.epochMonthToMMMYY(selectedEpochMonth)} Youngworld OTB (${hierarchyKey})`}
      </h2>
      <div className="w-full flex flex-wrap items-center justify-end pr-4 mb-2 gap-2">
        <div className="flex items-center gap-2 mr-auto pl-2">
          <label className="text-sm">Month:</label>
          <MonthSelector
            epochMonth={selectedEpochMonth}
            setEpochMonth={(v: number) => setSelectedEpochMonth(Math.min(v, maxSelectableEpochMonth))}
            maxMonth={maxSelectableEpochMonth}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Hierarchy:</label>
          <select
            className="h-9 px-2 border rounded-md text-sm"
            value={hierarchyKey}
            onChange={(e) => setHierarchyKey(e.target.value as keyof typeof HIERARCHY_OPTIONS)}
          >
            {Object.keys(HIERARCHY_OPTIONS).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Employee:</label>
          <select
            className="h-9 px-2 border rounded-md text-sm"
            value={employee}
            onChange={(e) => setEmployee(e.target.value as EmployeeFilter)}
          >
            <option value="none">All</option>
            <option value="Lisa">Lisa</option>
            <option value="Hector">Hector</option>
            <option value="Aaron">Aaron</option>
          </select>
        </div>
        <button
          onClick={exportToExcel}
          className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow transition"
        >
          Download Excel
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="w-full h-full">
          <ReportTable
            columns={columns}
            data={tableData}
            headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
            stickyHeader={true}
          />
        </div>
      )}
    </div>
  );
};

export default YoungworldOtbReport;

